/**
 * LivePreviewPanel — composer right-rail wrapper around `InvoicePreview`.
 *
 * Keeps the implementation behavior (live computed data, expand modal,
 * browser print/download fallback, send-channel summary) while matching
 * `prototype/sales/invoice-new.html`'s preview card structure.
 *
 * @see prototype/sales/invoice-new.html — `.preview-card`
 */
import { useState } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Download, Maximize2, MessageCircle, Printer, X } from "lucide-react";
import { Button, DialogRoot, DialogContent, DialogHeader, DialogTitle } from "@flint/ui";
import { InvoicePreview, type InvoicePreviewProps } from "./invoice-preview.tsx";
import { formatPaise } from "./lib/format-money.ts";

export interface LivePreviewPanelProps extends InvoicePreviewProps {
  /** Channel the operator picked in section 4 — surfaces in the summary cells. */
  sendChannel?: "whatsapp" | "email" | "print-later" | "issue-only";
  /** Dialog title. Defaults to invoice wording; proformas override it. */
  previewTitle?: string | undefined;
  /** Summary value shown when no send channel exists, e.g. proforma draft. */
  documentSummary?: string | undefined;
}

type SendChannel = NonNullable<LivePreviewPanelProps["sendChannel"]>;

const SEND_CHANNEL_LABEL: Record<SendChannel, string> = {
  whatsapp: "WhatsApp",
  email: "Email",
  "print-later": "Print later",
  "issue-only": "Issue only",
};

export function LivePreviewPanel(props: LivePreviewPanelProps) {
  const [fullOpen, setFullOpen] = useState(false);
  const effectiveStatus = props.status ?? (props.docNumber ? "issued" : "draft");
  const previewTitle = props.previewTitle ?? "Invoice preview";

  return (
    <>
      <div className="overflow-hidden rounded-md border border-line bg-paper shadow-md">
        <header className="flex items-center justify-between gap-3 border-b border-line bg-raised px-4 py-3.5">
          <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
            <span aria-hidden className="h-2 w-2 rounded-full bg-emerald-600 shadow-rail" />
            Live preview
          </h3>
          <div className="flex items-center gap-3">
            <PreviewToolButton type="button" onClick={() => setFullOpen(true)}>
              <Maximize2 size={12} />
              <span>Expand</span>
            </PreviewToolButton>
            <PreviewToolButton
              type="button"
              onClick={() => window.print()}
              title="Browser print dialog · pick Save-as-PDF for a copy"
            >
              <Download size={12} />
              <span>Download PDF</span>
            </PreviewToolButton>
          </div>
        </header>

        <div className="px-5 py-5">
          <InvoicePreview {...props} chrome="plain" />
        </div>

        <PreviewSummaryStrip
          status={effectiveStatus}
          sendChannel={props.sendChannel}
          documentSummary={props.documentSummary}
          taxModeLabel={props.totals.taxMode === "intra" ? "CGST + SGST" : "IGST"}
          grandTotalPaise={props.totals.grandTotalPaise}
        />
      </div>

      <DialogRoot open={fullOpen} onOpenChange={setFullOpen}>
        <DialogContent className="max-h-screen overflow-y-auto p-0 sm:max-w-4xl">
          <DialogHeader className="flex flex-row items-center justify-between gap-3 border-b border-line p-4">
            <DialogTitle className="inline-flex items-center gap-2">
              {previewTitle}
              <span className="rounded-full border border-line bg-raised px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-muted">
                Draft
              </span>
            </DialogTitle>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                intent="ghost"
                size="sm"
                onClick={() => window.print()}
                title="Browser print dialog · pick Save-as-PDF for a copy"
              >
                <Printer size={14} />
                <span>Print</span>
              </Button>
              <Button
                type="button"
                intent="ghost"
                size="sm"
                onClick={() => setFullOpen(false)}
                aria-label="Close"
              >
                <X size={14} />
              </Button>
            </div>
          </DialogHeader>
          <div className="p-5">
            <InvoicePreview {...props} />
          </div>
        </DialogContent>
      </DialogRoot>
    </>
  );
}

function PreviewToolButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline"
    />
  );
}

function PreviewSummaryStrip({
  status,
  sendChannel,
  documentSummary,
  taxModeLabel,
  grandTotalPaise,
}: {
  status: NonNullable<InvoicePreviewProps["status"]>;
  sendChannel?: LivePreviewPanelProps["sendChannel"];
  documentSummary?: string | undefined;
  taxModeLabel: string;
  grandTotalPaise: bigint;
}) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-line bg-paper px-4 py-3.5">
      <Cell label="Status" value={statusSummary(status)} />
      <Cell
        label={sendChannel ? "Send via" : "Document"}
        value={
          sendChannel ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
              <MessageCircle size={11} />
              {SEND_CHANNEL_LABEL[sendChannel]}
            </span>
          ) : (
            documentSummary ?? "Draft"
          )
        }
      />
      <Cell label="Tax mode" value={taxModeLabel} />
      <Cell label="Grand total" value={`₹${formatPaise(grandTotalPaise)}`} mono emphasised />
    </div>
  );
}

function Cell({
  label,
  value,
  mono,
  emphasised,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  emphasised?: boolean;
}) {
  return (
    <div>
      <p className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-muted">{label}</p>
      <p
        className={[
          "text-sm",
          mono ? "font-mono tabular-nums" : "",
          emphasised ? "font-bold text-ink" : "font-medium text-ink",
        ].join(" ")}
      >
        {value}
      </p>
    </div>
  );
}

function statusSummary(status: NonNullable<InvoicePreviewProps["status"]>): string {
  switch (status) {
    case "draft":
      return "Draft · pending number";
    case "issued":
      return "Issued";
    case "partially_paid":
      return "Partial";
    case "paid":
      return "Paid";
    case "overdue":
      return "Overdue";
    case "void":
      return "Voided";
  }
}
